import React, { useContext, useState } from 'react'

import { Link } from '@driftt/dds'
import { P } from '@driftt/tide-core'

import { AccountActivityUserAvatar } from 'components/Avatars'
import EngagementScoreIndicator, {
  EngagementScoreIndicatorVariant,
  scoreToIndicator
} from 'components/EngagementScoreIndicator'
import { EngagementScoreOverview } from 'components/Explain'
import { AccountDrawerContext } from 'context/accountDrawerContext'
import { Contact, Insight } from 'modules/accountActivity/domain'
import { pluralize } from 'utils/formatStrings'
import { accountPath, contactPath } from 'utils/paths'
import { segmentTrack, TrackType } from 'utils/tracking'

import CardHeader from '../CardHeader'

import utilityStyles from '../../../../styles/utility.module.css'
import cardStyles from '../styles.module.css'
import styles from './styles.module.css'

interface EngagementScoreCardProps {
  insight: Insight
}

interface ActivitySummaryProps {
  insight: Insight
}

interface ContactProps {
  contact: Contact
}

const CONTACT_CUTOFF = 3

const getLocationString = (contact: Contact) => {
  return [contact.city, contact.state].filter(Boolean).join(', ')
}

const indicatorToText = (indicator: EngagementScoreIndicatorVariant): string => {
  switch (indicator) {
    case 'positive':
      return 'increased'
    case 'negative':
      return 'decreased'
    case 'neutral':
      return 'stayed the same'
    default:
      return ''
  }
}

const ActivitySummary: React.FC<ActivitySummaryProps> = ({ insight }) => {
  const { accountData, companyData } = insight
  const score = insight.body.normalizedEngagementScore
  const prevScore = insight.body.lastNormalizedEngagementScore
  const change = scoreToIndicator(score, prevScore)
  const {setIsOpen: setIsDrawerOpen, setAccountId: setSelectedAccountId} = useContext(AccountDrawerContext)

  const accountDisplayName = accountData?.name || companyData?.domain || 'Unnamed account'
  return (
    <div>
      <Link 
        className={styles.displayName}
        onClick={() => {
          setIsDrawerOpen(true)
          setSelectedAccountId(insight.accountId)
        }}>
        {accountDisplayName}
      </Link>
      &apos;s engagement score{' '}
      <span className={utilityStyles.semiBoldWeight}>{indicatorToText(change)}</span>{' '}
      <EngagementScoreIndicator
        variant={change}
        style={{
          margin: 0
        }}
      />{' '}
      based on activity from{' '}
      <span className={utilityStyles.semiBoldWeight}>
        {insight.body.contacts.length} {pluralize('contact', insight.body.contacts.length)}
      </span>
    </div>
  )
}

const ContactItem: React.FC<ContactProps> = ({ contact }) => {
  const handleClick = () => segmentTrack(TrackType.ENGAGEMENT_SCORE_CARD_VIEW_CONTACT_CLICK)

  return (
    <div className={styles.contact}>
      <AccountActivityUserAvatar
        imageUrl={contact.avatarUrl}
        size="x-small"
        className={styles.contactAvatar}
      />
      <Link
        className={cardStyles.linkStyle}
        href={contactPath(contact?.contactId)}
        onClick={handleClick}>
        {contact.name || contact.email || 'Site visitor'}
      </Link>
      <div className={styles.contactInfo}>
        {contact.employmentTitle || getLocationString(contact)}
      </div>
    </div>
  )
}

const EngagementScoreCard: React.FC<EngagementScoreCardProps> = ({ insight }) => {
  const [showMore, setShowMore] = useState(false)
  const cutoff = showMore ? insight.body?.contacts?.length : CONTACT_CUTOFF
  const leftover = insight.body?.contacts?.length - cutoff

  const handleGotoAccount = () => segmentTrack(TrackType.ENGAGEMENT_SCORE_CARD_VIEW_ACTIVITY_CLICK)

  const handleShowMore = () => {
    segmentTrack(TrackType.ENGAGEMENT_SCORE_CARD_SEE_MORE_CONTACTS_CLICK)
    setShowMore(true)
  }

  const contactsLabel: string = `active ${pluralize(
    'contact',
    insight?.body?.contacts?.length
  )}`.toUpperCase()

  return (
    <>
      <CardHeader
        insight={insight}
        title={<ActivitySummary insight={insight} />}
      />
      <EngagementScoreOverview
        radial
        score={insight.body.normalizedEngagementScore}
        prevScore={insight.body.lastNormalizedEngagementScore}
      />
      <div className={cardStyles.cardContent}>
        <P type="small" className={utilityStyles.semiBoldWeight}>
          {contactsLabel}
        </P>
        {insight.body.contacts.slice(0, cutoff).map((contact: any) => (
          <ContactItem key={contact.contactId} contact={contact} />
        ))}
        {!showMore && leftover > 0 && (
          <div className={styles.seeMore} onClick={handleShowMore}>
            + {leftover} {pluralize('other', leftover)}
          </div>
        )}
        {showMore && (
          <Link
            href={accountPath(insight.accountId)}
            className={styles.seeMore}
            onClick={handleGotoAccount}>
            See all activity
          </Link>
        )}
      </div>
    </>
  )
}

export default EngagementScoreCard
